import React, { useState } from "react";
import {
  memberData,
  revenueData,
  eventData,
  emailData,
  invoicingData,
  websiteData,
  months,
} from "../MyChamber/analyticsData";
import {
  AreaChart,
  BarChart,
  PieChart,
  StatCard,
} from "../MyChamber/ChartComponents";

// Types
type ReportCategory =
  | "membership"
  | "financial"
  | "events"
  | "engagement"
  | "website";

interface ReportOption {
  id: string;
  title: string;
  description: string;
  category: ReportCategory;
  renderReport: () => JSX.Element;
}

const Reports = () => {
  // State
  const [selectedCategory, setSelectedCategory] =
    useState<ReportCategory>("membership");
  const [selectedReport, setSelectedReport] = useState<ReportOption | null>(
    null
  );
  const [showReport, setShowReport] = useState(false);

  // Report options
  const reportOptions: ReportOption[] = [
    // Membership reports
    {
      id: "membership-overview",
      title: "Membership Overview",
      description: "Summary of your chamber membership statistics",
      category: "membership",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title="Total Members" value={memberData.totalMembers} />
            <StatCard title="Active Members" value={memberData.activeMembers} />
            <StatCard
              title="New This Month"
              value={memberData.newMembersThisMonth}
            />
            <StatCard
              title="Retention Rate"
              value={memberData.memberRetentionRate}
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Member Size Breakdown
            </h3>
            <PieChart
              segments={memberData.memberBreakdown}
              height={350}
              segmentValueLabel="members"
            />
          </div>
        </div>
      ),
    },
    {
      id: "new-member-acquisition",
      title: "New Member Acquisition",
      description: "Analysis of new member growth over the past year",
      category: "membership",
      renderReport: () => (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              New Member Trend
            </h3>
            <BarChart
              data={memberData.newMembersMonthly}
              categories={months}
              height={350}
            />

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">
                  Total New Members (Year)
                </p>
                <p className="text-xl font-bold text-cpaGold">
                  {memberData.newMembersMonthly[0].data.reduce(
                    (a, b) => a + b,
                    0
                  )}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">Best Month</p>
                <p className="text-xl font-bold text-cpaGold">
                  {
                    months[
                      memberData.newMembersMonthly[0].data.indexOf(
                        Math.max(...memberData.newMembersMonthly[0].data)
                      )
                    ]
                  }
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">Average Monthly</p>
                <p className="text-xl font-bold text-cpaGold">
                  {Math.round(
                    memberData.newMembersMonthly[0].data.reduce(
                      (a, b) => a + b,
                      0
                    ) / 12
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "member-engagement",
      title: "Member Engagement",
      description: "Track how active your members are with chamber activities",
      category: "membership",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Member Engagement Trend
          </h3>
          <AreaChart
            series={[memberData.memberEngagement]}
            labels={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Current Engagement</p>
              <p className="text-xl font-bold text-cpaGold">
                {
                  memberData.memberEngagement.data[
                    memberData.memberEngagement.data.length - 1
                  ]
                }
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Growth (Year)</p>
              <p className="text-xl font-bold text-cpaGold">
                {memberData.memberEngagement.data[
                  memberData.memberEngagement.data.length - 1
                ] - memberData.memberEngagement.data[0]}
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Average</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  memberData.memberEngagement.data.reduce((a, b) => a + b, 0) /
                    memberData.memberEngagement.data.length
                )}
                %
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Financial reports
    {
      id: "revenue-breakdown",
      title: "Revenue Breakdown",
      description: "Analysis of your chamber's revenue sources",
      category: "financial",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard title="Total Revenue" value={revenueData.total} />
            <StatCard
              title="Membership Revenue"
              value={revenueData.membership}
            />
            <StatCard title="Event Revenue" value={revenueData.events} />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Revenue Breakdown
            </h3>
            <PieChart
              segments={[
                {
                  name: "Membership",
                  value: parseFloat(
                    revenueData.membership.replace(/[^0-9.]/g, "")
                  ),
                  color: "#E1A731",
                },
                {
                  name: "Events",
                  value: parseFloat(revenueData.events.replace(/[^0-9.]/g, "")),
                  color: "#D4A82B",
                },
              ]}
              height={350}
              segmentValueLabel="dollars"
            />
          </div>
        </div>
      ),
    },
    {
      id: "monthly-revenue",
      title: "Monthly Revenue Trend",
      description: "Track your revenue growth over the past year",
      category: "financial",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Monthly Revenue
          </h3>
          <AreaChart
            series={[revenueData.membershipSeries, revenueData.eventsSeries]}
            labels={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Highest Revenue Month</p>
              <p className="text-xl font-bold text-cpaGold">{months[11]}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Monthly Growth (Avg)</p>
              <p className="text-xl font-bold text-cpaGold">
                +
                {Math.round(
                  (revenueData.membershipSeries.data[11] +
                    revenueData.eventsSeries.data[11] -
                    (revenueData.membershipSeries.data[0] +
                      revenueData.eventsSeries.data[0])) /
                    12
                )}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Annual Growth</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  ((revenueData.membershipSeries.data[11] +
                    revenueData.eventsSeries.data[11] -
                    (revenueData.membershipSeries.data[0] +
                      revenueData.eventsSeries.data[0])) /
                    (revenueData.membershipSeries.data[0] +
                      revenueData.eventsSeries.data[0])) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "invoicing-status",
      title: "Invoicing Status",
      description: "Summary of paid and receivable invoices",
      category: "financial",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Invoiced"
              value={invoicingData.totalInvoiced}
            />
            <StatCard title="Total Paid" value={invoicingData.totalPaid} />
            <StatCard title="Receivable" value={invoicingData.outstanding} />
            <StatCard title="Overdue" value={invoicingData.overdue} />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Payment Status
            </h3>
            <PieChart
              segments={invoicingData.paymentStatus}
              height={350}
              segmentValueLabel="dollars"
            />

            <div className="mt-6">
              <h4 className="text-md font-medium text-gray-800 mb-2">
                Collection Rate
              </h4>
              <div className="bg-gray-200 h-4 rounded-full overflow-hidden">
                <div
                  className="bg-cpaGold h-full rounded-full"
                  style={{
                    width: `${Math.round(
                      (parseFloat(
                        invoicingData.totalPaid.replace(/[^0-9.]/g, "")
                      ) /
                        parseFloat(
                          invoicingData.totalInvoiced.replace(/[^0-9.]/g, "")
                        )) *
                        100
                    )}%`,
                  }}
                ></div>
              </div>
              <p className="text-sm text-right mt-1">
                {Math.round(
                  (parseFloat(invoicingData.totalPaid.replace(/[^0-9.]/g, "")) /
                    parseFloat(
                      invoicingData.totalInvoiced.replace(/[^0-9.]/g, "")
                    )) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Events reports
    {
      id: "event-performance",
      title: "Event Performance",
      description: "Overview of event attendance and revenue",
      category: "events",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title="Total Events" value={eventData.totalEvents} />
            <StatCard
              title="Upcoming Events"
              value={eventData.upcomingEvents}
            />
            <StatCard
              title="Average Attendance"
              value={eventData.averageAttendance}
            />
            <StatCard title="Event Revenue" value={eventData.eventRevenue} />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Event Types
            </h3>
            <PieChart
              segments={eventData.eventsByType}
              height={350}
              segmentValueLabel="events"
            />
          </div>
        </div>
      ),
    },
    {
      id: "event-attendance",
      title: "Event Attendance",
      description: "Track attendance trends across all events",
      category: "events",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Event Attendance
          </h3>
          <BarChart
            data={eventData.attendanceSeries}
            categories={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Total Attendance (Year)</p>
              <p className="text-xl font-bold text-cpaGold">
                {eventData.attendanceSeries
                  .reduce(
                    (sum, series) =>
                      sum + series.data.reduce((a, b) => a + b, 0),
                    0
                  )
                  .toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">In-Person Ratio</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  (eventData.attendanceSeries[0].data.reduce(
                    (a, b) => a + b,
                    0
                  ) /
                    eventData.attendanceSeries.reduce(
                      (sum, series) =>
                        sum + series.data.reduce((a, b) => a + b, 0),
                      0
                    )) *
                    100
                )}
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Virtual Ratio</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  (eventData.attendanceSeries[1].data.reduce(
                    (a, b) => a + b,
                    0
                  ) /
                    eventData.attendanceSeries.reduce(
                      (sum, series) =>
                        sum + series.data.reduce((a, b) => a + b, 0),
                      0
                    )) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "ticket-sales",
      title: "Ticket Sales",
      description: "Analysis of ticket sales for events",
      category: "events",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Ticket Sales Trend
          </h3>
          <AreaChart
            series={[eventData.ticketSales]}
            labels={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Total Tickets Sold</p>
              <p className="text-xl font-bold text-cpaGold">
                {eventData.ticketSales.data
                  .reduce((a, b) => a + b, 0)
                  .toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Best Month</p>
              <p className="text-xl font-bold text-cpaGold">
                {
                  months[
                    eventData.ticketSales.data.indexOf(
                      Math.max(...eventData.ticketSales.data)
                    )
                  ]
                }
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Growth (Year)</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  ((eventData.ticketSales.data[11] -
                    eventData.ticketSales.data[0]) /
                    eventData.ticketSales.data[0]) *
                    100
                )}
                %
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Engagement reports
    {
      id: "email-performance",
      title: "Email Performance",
      description: "Analysis of your email campaign effectiveness",
      category: "engagement",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard title="Total Emails" value={emailData.totalEmails} />
            <StatCard title="Open Rate" value={emailData.openRate} />
            <StatCard title="Click Rate" value={emailData.clickThroughRate} />
            <StatCard
              title="Avg Response Time"
              value={emailData.averageResponseTime}
            />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Email Types
            </h3>
            <PieChart
              segments={emailData.emailsByType}
              height={350}
              segmentValueLabel="emails"
            />
          </div>
        </div>
      ),
    },
    {
      id: "email-engagement",
      title: "Email Engagement Trend",
      description: "Track open and click rates over the past year",
      category: "engagement",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Email Engagement
          </h3>
          <AreaChart
            series={[
              emailData.emailEngagement.openRateSeries,
              emailData.emailEngagement.clickRateSeries,
            ]}
            labels={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Open Rate Growth</p>
              <p className="text-xl font-bold text-cpaGold">
                {emailData.emailEngagement.openRateSeries.data[11] -
                  emailData.emailEngagement.openRateSeries.data[0]}
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Click Rate Growth</p>
              <p className="text-xl font-bold text-cpaGold">
                {(
                  emailData.emailEngagement.clickRateSeries.data[11] -
                  emailData.emailEngagement.clickRateSeries.data[0]
                ).toFixed(1)}
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Best Month</p>
              <p className="text-xl font-bold text-cpaGold">
                {
                  months[
                    emailData.emailEngagement.openRateSeries.data.indexOf(
                      Math.max(...emailData.emailEngagement.openRateSeries.data)
                    )
                  ]
                }
              </p>
            </div>
          </div>
        </div>
      ),
    },

    // Website reports
    {
      id: "website-traffic",
      title: "Website Traffic",
      description: "Analysis of your website traffic and visitor data",
      category: "website",
      renderReport: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Visits"
              value={websiteData.totalVisits.toLocaleString()}
            />
            <StatCard
              title="Unique Visitors"
              value={websiteData.uniqueVisitors.toLocaleString()}
            />
            <StatCard
              title="Avg Session Duration"
              value={websiteData.averageSessionDuration}
            />
            <StatCard title="Bounce Rate" value={websiteData.bounceRate} />
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Traffic Sources
            </h3>
            <PieChart
              segments={websiteData.trafficSources.map((source) => ({
                ...source,
                value: source.value,
              }))}
              height={350}
              segmentValueLabel="visits"
            />
          </div>
        </div>
      ),
    },
    {
      id: "website-visitor-trend",
      title: "Website Visitor Trend",
      description: "Track website traffic over the past year",
      category: "website",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Monthly Visitor Trend
          </h3>
          <AreaChart
            series={[websiteData.visitorTrend]}
            labels={months}
            height={350}
          />

          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Growth (Year)</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  ((websiteData.visitorTrend.data[11] -
                    websiteData.visitorTrend.data[0]) /
                    websiteData.visitorTrend.data[0]) *
                    100
                )}
                %
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Best Month</p>
              <p className="text-xl font-bold text-cpaGold">
                {
                  months[
                    websiteData.visitorTrend.data.indexOf(
                      Math.max(...websiteData.visitorTrend.data)
                    )
                  ]
                }
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-sm text-gray-500">Average Monthly</p>
              <p className="text-xl font-bold text-cpaGold">
                {Math.round(
                  websiteData.visitorTrend.data.reduce((a, b) => a + b, 0) / 12
                ).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "popular-pages",
      title: "Most Popular Pages",
      description: "See which pages on your website get the most traffic",
      category: "website",
      renderReport: () => (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Top Pages by Visits
          </h3>

          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                  >
                    Page
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                  >
                    Visits
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                  >
                    % of Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {websiteData.topPages.map((page, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {page.page}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                      {page.visits.toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500">
                      {Math.round(
                        (page.visits / websiteData.totalVisits) * 100
                      )}
                      %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6">
            <h4 className="text-md font-medium text-gray-800 mb-2">
              Page Distribution
            </h4>
            <div className="flex h-4 rounded-full overflow-hidden">
              {websiteData.topPages.map((page, i) => (
                <div
                  key={i}
                  className="h-full"
                  style={{
                    width: `${Math.round(
                      (page.visits / websiteData.totalVisits) * 100
                    )}%`,
                    backgroundColor: [
                      "#E1A731",
                      "#D4A82B",
                      "#F0CA83",
                      "#F8E3B5",
                      "#EFD499",
                    ][i % 5],
                  }}
                ></div>
              ))}
            </div>
            <div className="flex justify-between mt-2">
              {websiteData.topPages.map((page, i) => (
                <div key={i} className="flex items-center text-xs">
                  <div
                    className="w-2 h-2 mr-1 rounded-full"
                    style={{
                      backgroundColor: [
                        "#E1A731",
                        "#D4A82B",
                        "#F0CA83",
                        "#F8E3B5",
                        "#EFD499",
                      ][i % 5],
                    }}
                  ></div>
                  {page.page}
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Filter reports by category
  const filteredReports = reportOptions.filter(
    (report) => report.category === selectedCategory
  );

  // Handle generate report
  const handleGenerateReport = () => {
    if (selectedReport) {
      setShowReport(true);
    }
  };

  // Get category name for display
  const getCategoryName = (category: ReportCategory): string => {
    switch (category) {
      case "membership":
        return "Membership";
      case "financial":
        return "Financial";
      case "events":
        return "Events";
      case "engagement":
        return "Engagement";
      case "website":
        return "Website";
      default:
        return "";
    }
  };

  // Categories
  const categories: ReportCategory[] = [
    "membership",
    "financial",
    "events",
    "engagement",
    "website",
  ];

  return (
    <div className="">
      {/* Report selection */}
      {!showReport && (
        <div className="mb-8">
          {/* Category tabs */}
          <div className="border-b border-gray-200">
            <nav
              className="-mb-px flex space-x-8 overflow-x-auto"
              aria-label="Report categories"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => {
                    setSelectedCategory(category);
                    setSelectedReport(null);
                  }}
                  className={`
                    whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm
                    ${
                      selectedCategory === category
                        ? "border-cpaGold text-cpaGold"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }
                  `}
                  aria-current={
                    selectedCategory === category ? "page" : undefined
                  }
                >
                  {getCategoryName(category)}
                </button>
              ))}
            </nav>
          </div>

          {/* Report options */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReports.map((report) => (
              <div
                key={report.id}
                onClick={() => setSelectedReport(report)}
                className={`
                  cursor-pointer rounded-lg border p-6 shadow-sm hover:shadow-md transition-shadow
                  ${
                    selectedReport?.id === report.id
                      ? "border-cpaGold ring-2 ring-cpaGold"
                      : "border-gray-200"
                  }
                `}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {report.title}
                  </h3>
                  {selectedReport?.id === report.id && (
                    <span className="text-cpaGold">
                      <svg
                        className="h-5 w-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500">{report.description}</p>
              </div>
            ))}
          </div>

          {/* Generate button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleGenerateReport}
              disabled={!selectedReport}
              className={`
                px-4 py-2 rounded-md text-white font-medium shadow-sm focus:outline-none
                ${
                  selectedReport
                    ? "bg-cpaGold hover:bg-amber-600"
                    : "bg-gray-300 cursor-not-allowed"
                }
              `}
            >
              Generate Report
            </button>
          </div>
        </div>
      )}

      {/* Report display */}
      {showReport && selectedReport && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              {selectedReport.title}
            </h2>
            <button
              onClick={() => setShowReport(false)}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center"
            >
              <svg
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Reports
            </button>
          </div>

          {/* Render the selected report */}
          {selectedReport.renderReport()}

          {/* Export options (dummy UI) */}
          <div className="mt-8 flex justify-end space-x-4">
            <button className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-gray-50 focus:outline-none">
              Export as PDF
            </button>
            <button className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-gray-50 focus:outline-none">
              Export as CSV
            </button>
            <button className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 font-medium shadow-sm hover:bg-gray-50 focus:outline-none">
              Print Report
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
